import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Search, ArrowRight, Heart } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { adoptionsApi } from "@/lib/api";

export default function Adoption() {
    const [params, setParams] = useSearchParams();
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const activeCat = params.get("category") || "";
    const [q, setQ] = useState(params.get("q") || "");
    const [plz, setPlz] = useState(params.get("plz") || "");

    useEffect(() => {
        adoptionsApi.categories().then((d) => setCats(d.categories || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const filters = {};
        if (activeCat) filters.category = activeCat;
        if (q) filters.q = q;
        if (plz) filters.plz = plz;
        setLoading(true);
        adoptionsApi
            .list(filters)
            .then((d) => setItems(d.adoptions || []))
            .finally(() => setLoading(false));
    }, [activeCat, q, plz]);

    function selectCat(key) {
        const next = new URLSearchParams(params);
        if (!key || key === activeCat) next.delete("category");
        else next.set("category", key);
        setParams(next);
    }

    function applyFilters() {
        const next = new URLSearchParams(params);
        if (q) next.set("q", q);
        else next.delete("q");
        if (plz) next.set("plz", plz);
        else next.delete("plz");
        setParams(next);
    }

    return (
        <SiteLayout>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
                <div className="label mb-4">Taubenvermittlung</div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
                    Für jede Taube gibt es einen{" "}
                    <span className="italic text-[#f0c674]">passenden Menschen</span>.
                </h1>
                <p className="text-[#94a3b8] text-lg mt-6 max-w-2xl leading-relaxed">
                    Handicap-Tauben, flugfähige Tauben und Tauben, die einen Partner suchen.
                    Filter dich durch — vielleicht wartet hier jemand auf dich.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/vermittlung/inserat-aufgeben" className="btn-primary" data-testid="adoption-new-cta">
                        Inserat aufgeben
                    </Link>
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => {
                            const id = window.prompt("Gib die Inserats-ID ein, für die du einen neuen Bearbeitungs-Link möchtest. Du findest sie in der URL deines Inserats (z. B. /vermittlung/ABC-123…).");
                            if (!id) return;
                            const email = window.prompt("Mit welcher E-Mail hast du das Inserat angelegt?");
                            if (!email) return;
                            import("@/lib/api").then(({ adoptionsApi }) =>
                                adoptionsApi.requestEditLink(id.trim(), email.trim())
                                    .then(() => alert("Falls die Daten passen, ist der Link unterwegs."))
                                    .catch(() => alert("Etwas ging schief. Bitte später erneut versuchen."))
                            );
                        }}
                        data-testid="adoption-request-link"
                    >
                        Bearbeitungs-Link verloren?
                    </button>
                </div>

                <div className="mt-10 flex gap-2 overflow-x-auto pb-2" data-testid="category-filters">
                    <button
                        className={`chip ${activeCat === "" ? "active" : ""}`}
                        onClick={() => selectCat("")}
                        data-testid="cat-all"
                    >
                        Alle
                    </button>
                    {cats.map((c) => (
                        <button
                            key={c.key}
                            className={`chip ${activeCat === c.key ? "active" : ""}`}
                            onClick={() => selectCat(c.key)}
                            data-testid={`cat-${c.key}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                            placeholder="Suche nach Name, Beschreibung, Ort…"
                            className="input-field pl-9"
                            data-testid="adoption-search"
                        />
                    </div>
                    <input
                        value={plz}
                        onChange={(e) => setPlz(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                        placeholder="PLZ"
                        className="input-field"
                        data-testid="adoption-plz"
                    />
                    <button type="button" onClick={applyFilters} className="btn-secondary" data-testid="adoption-search-btn">
                        Suchen
                    </button>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-20">
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="glass aspect-[4/5] animate-pulse" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="glass p-12 text-center">
                        <Heart size={28} className="text-[#f0c674] mx-auto mb-4" />
                        <p className="text-[#94a3b8]">
                            Aktuell keine Vermittlungen{activeCat ? " in dieser Kategorie" : ""}.
                            <br />
                            Schau später wieder vorbei.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((a, i) => (
                            <AdoptionCard key={a.id} a={a} index={i} catLabel={labelFor(cats, a.category)} />
                        ))}
                    </div>
                )}
            </section>
        </SiteLayout>
    );
}

function labelFor(cats, key) {
    return cats.find((c) => c.key === key)?.label || key;
}

function AdoptionCard({ a, index, catLabel }) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const imgUrl = a.image_count > 0 ? `${BACKEND}/api/adoptions/${a.id}/image/0` : null;
    const [src, setSrc] = useState(null);

    useEffect(() => {
        if (!imgUrl) return;
        let alive = true;
        adoptionsApi
            .image(a.id, 0)
            .then((d) => alive && setSrc(d.data))
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [a.id, imgUrl]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.35) }}
        >
            <Link to={`/vermittlung/${a.id}`} className="glass gold-glow group block overflow-hidden transition-all hover:-translate-y-1" data-testid={`adoption-card-${a.id}`}>
                <div className="aspect-[4/5] relative overflow-hidden">
                    {src ? (
                        <img src={src} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0c1222] to-[#1a2541] flex items-center justify-center">
                            <Heart size={32} className="text-[#f0c674]/40" />
                        </div>
                    )}
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-[#040710]/80 text-[#f0c674] border border-[#f0c674]/30">
                            {catLabel}
                        </span>
                        {a.flight_capable && (
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                                Flugfähig
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-5">
                    <h3 className="font-display text-xl mb-1">{a.title}</h3>
                    {(a.location || a.plz) && (
                        <div className="flex items-center gap-1.5 text-xs text-[#94a3b8] mb-3">
                            <MapPin size={11} />
                            {a.plz ? `${a.plz} ` : ""}
                            {a.location || ""}
                        </div>
                    )}
                    <p className="text-sm text-[#94a3b8] line-clamp-2 leading-relaxed">{a.description}</p>
                    <div className="mt-4 text-[#f0c674] text-xs inline-flex items-center gap-1.5">
                        Profil ansehen <ArrowRight size={12} />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
